import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, X } from 'lucide-react';
import { Button } from '../shared/Button';

interface PassCheckoutDialogProps {
  open: boolean;
  submitting: boolean;
  error: string | null;
  onSubmit: (email: string) => void;
  onClose: () => void;
}

/**
 * Collects the buyer email before an anonymous pass checkout. The backend needs
 * it to create a guest Stripe customer; a logged-in buyer never sees this.
 */
export const PassCheckoutDialog: React.FC<PassCheckoutDialogProps> = ({
  open,
  submitting,
  error,
  onSubmit,
  onClose,
}) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setEmail('');
      setLocalError(null);
    }
  }, [open]);

  if (!open) {
    return null;
  }

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setLocalError(t('validation.email'));
      return;
    }
    setLocalError(null);
    onSubmit(trimmed);
  };

  const shownError = localError || error;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={t('landing.pricing.passes.emailDialog.title')}
    >
      <div className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl md:p-8">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-ink">
              {t('landing.pricing.passes.emailDialog.title')}
            </h2>
            <p className="mt-1 text-sm text-slate-600">
              {t('landing.pricing.passes.emailDialog.description')}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
            aria-label={t('common.close')}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="pass-checkout-email" className="mb-1 block text-sm font-medium text-gray-700">
              {t('landing.pricing.passes.emailDialog.emailLabel')}
            </label>
            <input
              id="pass-checkout-email"
              type="email"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
                if (localError) setLocalError(null);
              }}
              placeholder={t('landing.pricing.passes.emailDialog.emailPlaceholder')}
              autoComplete="email"
              className={`w-full rounded-2xl border px-4 py-3 transition-colors focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 ${
                shownError ? 'border-error bg-error/5' : 'border-primary/20 bg-white'
              }`}
            />
          </div>

          {shownError && (
            <p className="rounded-2xl border border-error/25 bg-error/10 p-3 text-sm text-error">
              {shownError}
            </p>
          )}

          <div className="flex flex-col gap-2 sm:flex-row">
            <Button
              type="submit"
              variant="primary"
              className="w-full sm:flex-1"
              loading={submitting}
              disabled={submitting}
              icon={<Mail className="w-5 h-5" />}
            >
              {t('landing.pricing.passes.emailDialog.submit')}
            </Button>
            <Button
              type="button"
              variant="tertiary"
              className="w-full sm:w-auto"
              onClick={onClose}
              disabled={submitting}
            >
              {t('landing.pricing.passes.emailDialog.cancel')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PassCheckoutDialog;
